import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";

const number = (value) => Number(value || 0);

export const formatMoney = (value) =>
  number(value).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const escapeHtml = (value) =>
  String(value ?? "-")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");

const getTotals = (rows) => {
  const totalIn = rows.reduce((sum, row) => sum + number(row.amountIn), 0);
  const totalOut = rows.reduce((sum, row) => sum + number(row.amountOut), 0);
  return { totalIn, totalOut, net: totalIn - totalOut };
};

const reportPeriod = (filters = {}) => {
  if (filters.dateFrom && filters.dateTo) return `${filters.dateFrom} to ${filters.dateTo}`;
  if (filters.dateFrom) return `From ${filters.dateFrom}`;
  if (filters.dateTo) return `Up to ${filters.dateTo}`;
  return "All dates";
};

const safeName = (name) => name.replace(/[<>:"/\\|?*]+/g, "").trim();

export const printTransactionRegister = (rows, filters = {}, title = "Transaction Register") => {
  if (!rows.length) {
    window.alert("No transactions available to print.");
    return;
  }

  const { totalIn, totalOut, net } = getTotals(rows);
  const printWindow = window.open("", "_blank", "width=1200,height=850");
  if (!printWindow) {
    window.alert("Please allow pop-ups to print the transaction register.");
    return;
  }

  const tableRows = rows
    .map(
      (row, index) => `
        <tr>
          <td>${index + 1}</td>
          <td>${escapeHtml(row.date || "-")}</td>
          <td>${escapeHtml(row.accountName || "-")}</td>
          <td>${escapeHtml(row.expenseType || "-")}</td>
          <td>${escapeHtml(row.description || "-")}</td>
          <td class="num">${row.amountIn ? formatMoney(row.amountIn) : "-"}</td>
          <td class="num">${row.amountOut ? formatMoney(row.amountOut) : "-"}</td>
          <td>${escapeHtml(row.paidBy || "-")}</td>
          <td>${escapeHtml(row.comment || "-")}</td>
        </tr>`
    )
    .join("");

  printWindow.document.write(`<!doctype html>
<html>
<head>
  <meta charset="utf-8" />
  <title>${escapeHtml(title)}</title>
  <style>
    @page { size: A4 landscape; margin: 10mm; }
    body { font-family: Arial, sans-serif; color: #111; margin: 0; }
    h1 { font-size: 20px; margin: 0 0 5px; }
    .meta { font-size: 12px; margin-bottom: 12px; }
    table { width: 100%; border-collapse: collapse; font-size: 9px; }
    th, td { border: 1px solid #999; padding: 4px; vertical-align: top; }
    th { background: #e9eef7; text-align: left; }
    .num { text-align: right; white-space: nowrap; }
    .summary { margin-top: 12px; width: 340px; margin-left: auto; font-size: 12px; }
    .summary div { display: flex; justify-content: space-between; padding: 3px 0; }
    .summary .net { font-weight: bold; border-top: 1px solid #333; padding-top: 6px; }
    .footer { margin-top: 10px; font-size: 9px; color: #555; }
  </style>
</head>
<body>
  <h1>${escapeHtml(title)}</h1>
  <div class="meta">Period: ${escapeHtml(reportPeriod(filters))} &nbsp; | &nbsp; Records: ${rows.length}</div>
  <table>
    <thead><tr><th>#</th><th>Date</th><th>Account</th><th>Type</th><th>Description</th><th>Amount In</th><th>Amount Out</th><th>Paid By</th><th>Comment</th></tr></thead>
    <tbody>${tableRows}</tbody>
  </table>
  <div class="summary">
    <div><span>Total In</span><span>AED ${formatMoney(totalIn)}</span></div>
    <div><span>Total Out</span><span>AED ${formatMoney(totalOut)}</span></div>
    <div class="net"><span>Net Movement</span><span>AED ${formatMoney(net)}</span></div>
  </div>
  <div class="footer">Printed ${new Date().toLocaleString()}</div>
  <script>window.onload = () => { window.print(); };</script>
</body>
</html>`);
  printWindow.document.close();
};

export const exportTransactionRegisterPDF = (rows, filters = {}, title = "Transaction Register") => {
  if (!rows.length) {
    window.alert("No transactions available to export.");
    return;
  }

  const doc = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });
  const { totalIn, totalOut, net } = getTotals(rows);

  doc.setFont("helvetica", "bold");
  doc.setFontSize(16);
  doc.text(title, 10, 12);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  doc.text(`Period: ${reportPeriod(filters)} | Records: ${rows.length}`, 10, 19);

  autoTable(doc, {
    startY: 24,
    margin: { left: 6, right: 6 },
    head: [["#", "Date", "Account", "Type", "Description", "In", "Out", "Paid By", "Comment"]],
    body: rows.map((row, index) => [
      index + 1,
      row.date || "-",
      row.accountName || "-",
      row.expenseType || "-",
      row.description || "-",
      row.amountIn ? formatMoney(row.amountIn) : "-",
      row.amountOut ? formatMoney(row.amountOut) : "-",
      row.paidBy || "-",
      row.comment || "-",
    ]),
    styles: { fontSize: 7, cellPadding: 1.2, valign: "middle" },
    headStyles: { fillColor: [30, 77, 183], textColor: 255, fontStyle: "bold" },
    alternateRowStyles: { fillColor: [247, 247, 247] },
    columnStyles: { 0: { cellWidth: 8 }, 1: { cellWidth: 20 }, 5: { halign: "right" }, 6: { halign: "right" } },
  });

  let y = doc.lastAutoTable.finalY + 6;
  if (y > 185) {
    doc.addPage();
    y = 15;
  }
  doc.setFontSize(10);
  doc.text(`Total In: AED ${formatMoney(totalIn)}`, 200, y);
  doc.text(`Total Out: AED ${formatMoney(totalOut)}`, 200, y + 6);
  doc.setFont("helvetica", "bold");
  doc.text(`Net Movement: AED ${formatMoney(net)}`, 200, y + 12);

  const pages = doc.internal.getNumberOfPages();
  for (let page = 1; page <= pages; page += 1) {
    doc.setPage(page);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.text(`Page ${page} of ${pages}`, doc.internal.pageSize.getWidth() / 2, doc.internal.pageSize.getHeight() - 5, { align: "center" });
  }

  doc.save(safeName(`Transaction_Register_${filters.dateFrom || "All"}_to_${filters.dateTo || "All"}.pdf`));
};

export const exportTransactionRegisterExcel = async (rows, filters = {}, title = "Transaction Register") => {
  if (!rows.length) {
    window.alert("No transactions available to export.");
    return;
  }

  const workbook = new ExcelJS.Workbook();
  workbook.creator = "Comfort Line";
  workbook.created = new Date();
  const sheet = workbook.addWorksheet("Transactions", {
    views: [{ state: "frozen", ySplit: 4 }],
    properties: { defaultRowHeight: 18 },
  });

  sheet.mergeCells("A1:I1");
  sheet.getCell("A1").value = title;
  sheet.getCell("A1").font = { bold: true, size: 16 };
  sheet.mergeCells("A2:I2");
  sheet.getCell("A2").value = `Period: ${reportPeriod(filters)} | Records: ${rows.length}`;

  const header = sheet.addRow(["#", "Date", "Account", "Type", "Description", "Amount In", "Amount Out", "Paid By", "Comment"]);
  header.eachCell((cell) => {
    cell.font = { bold: true, color: { argb: "FFFFFFFF" } };
    cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FF1E4DB7" } };
    cell.alignment = { horizontal: "center", vertical: "middle", wrapText: true };
    cell.border = { top: { style: "thin" }, left: { style: "thin" }, bottom: { style: "thin" }, right: { style: "thin" } };
  });

  sheet.columns = [
    { width: 7 }, { width: 13 }, { width: 30 }, { width: 24 }, { width: 48 },
    { width: 15 }, { width: 15 }, { width: 22 }, { width: 32 },
  ];

  rows.forEach((row, index) => {
    const excelRow = sheet.addRow([
      index + 1,
      row.date || "-",
      row.accountName || "-",
      row.expenseType || "-",
      row.description || "-",
      number(row.amountIn) || null,
      number(row.amountOut) || null,
      row.paidBy || "-",
      row.comment || "-",
    ]);
    excelRow.eachCell((cell, col) => {
      cell.alignment = { vertical: "top", horizontal: col === 6 || col === 7 ? "right" : "left", wrapText: true };
      cell.border = {
        top: { style: "thin", color: { argb: "FFD0D0D0" } },
        left: { style: "thin", color: { argb: "FFD0D0D0" } },
        bottom: { style: "thin", color: { argb: "FFD0D0D0" } },
        right: { style: "thin", color: { argb: "FFD0D0D0" } },
      };
    });
    excelRow.getCell(6).numFmt = "#,##0.00";
    excelRow.getCell(7).numFmt = "#,##0.00";
  });

  const { totalIn, totalOut, net } = getTotals(rows);
  sheet.addRow([]);
  [["Total In", totalIn], ["Total Out", totalOut], ["Net Movement", net]].forEach(([label, amount]) => {
    const row = sheet.addRow(["", "", "", "", label, amount]);
    row.getCell(5).font = { bold: true };
    row.getCell(6).font = { bold: true };
    row.getCell(6).numFmt = "#,##0.00";
  });

  const buffer = await workbook.xlsx.writeBuffer();
  saveAs(
    new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }),
    safeName(`Transaction_Register_${filters.dateFrom || "All"}_to_${filters.dateTo || "All"}.xlsx`)
  );
};
